import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { dashboardApi } from "@/lib/phase3Entities";
import DashboardGreeting from "@/components/DashboardGreeting";
import DashboardGrid from "@/components/DashboardGrid";
import AddWidgetPicker from "@/components/AddWidgetPicker";
import FutureShopShell from "@/components/widgets/FutureShopShell";
import {
  Signal, LayoutDashboard, Pencil, Plus, RotateCcw, Save, X,
} from "lucide-react";
import {
  layoutFromLegacy,
  layoutFromServer,
  layoutsEqual,
  addWidget,
  removeWidget,
  setSize as setSizeInLayout,
  setHeight as setHeightInLayout,
  layoutToLegacy,
} from "@/lib/dashboardLayout";

/**
 * Dashboard — Phase C Wave 2A.1.
 *
 * Adds Edit Mode on top of the Wave 1/2A grid:
 *   - Toggle Edit Mode from the header
 *   - Reorder via drag OR accessible Up/Down buttons
 *   - Resize per-widget (Compact/Standard/Wide) where supported
 *   - Add widgets from a permission-filtered picker
 *   - Remove widgets
 *   - Save → wire contract only (feature-flagged NO-OP server-side)
 *   - Cancel → revert to layout captured on Edit-Mode entry
 *   - Restore Role Default → fetch role default (preview-only)
 *
 * FutureShopShell activates for `is_future_shop_role` role classes and
 * bypasses the grid entirely.
 */

const LiveTimestamp = ({ asOf }) => {
  if (!asOf) return null;
  let t = "";
  try {
    t = new Date(asOf).toLocaleTimeString(undefined, {
      hour: "2-digit", minute: "2-digit",
    });
  } catch { /* noop */ }
  return (
    <span className="text-[10.5px] uppercase tracking-widest text-steel" data-testid="dashboard-as-of">
      <Signal size={12} className="inline mr-1 text-touring-lime" /> Live · {t}
    </span>
  );
};

export default function Dashboard() {
  const { user, allowedPerms: authAllowedPerms } = useAuth();

  const [config, setConfig] = useState(null);
  const [configErr, setConfigErr] = useState(null);

  // The saved (server-truth) layout for this session. On non-edit mode
  // this is the source used to fetch and render.
  const [savedLayout, setSavedLayout] = useState([]);
  // The pending layout during Edit Mode; equal to savedLayout when idle.
  const [pendingLayout, setPendingLayout] = useState([]);
  const [editMode, setEditMode] = useState(false);
  const [preEditLayout, setPreEditLayout] = useState(null);
  const [showPicker, setShowPicker] = useState(false);
  const [saving, setSaving] = useState(false);
  // Wave 4B-3 P1: allowedPerms is now derived (line ~78) from
  // AuthContext.allowedPerms to avoid a duplicate /dashboard/permissions/me
  // request. No local state needed.

  const [payloads, setPayloads] = useState({});
  const [asOf, setAsOf] = useState(null);
  const [loadingSummary, setLoadingSummary] = useState(false);

  // Wave 4B-3 P1: Do NOT refetch /dashboard/permissions/me here — AuthContext
  // has already loaded it once. Reuse `authAllowedPerms` (a Set) as an array.
  const allowedPerms = useMemo(
    () => Array.from(authAllowedPerms || []),
    [authAllowedPerms],
  );

  // Wave 4B-3 P1: guard against React StrictMode double-invoke in dev.
  const didInitMeRef = useRef(false);
  useEffect(() => {
    if (didInitMeRef.current) return;
    didInitMeRef.current = true;
    dashboardApi.me()
      .then((r) => {
        setConfig(r);
        // Fleet Map UX Hardening — width persistence bug fix.
        // The server now returns a rich `layout` list preserving every
        // saved `size` (and, for opt-in widgets, `height`). Previously
        // this component fell through to `layoutFromLegacy(r.widgets)`
        // which passes only the widget-key strings, resetting every
        // `size` to "standard" and silently discarding the user's saved
        // Wide/Extra Tall picks on every page load. Read the rich
        // shape when available and only fall back to the legacy path
        // when the server explicitly returns no layout at all.
        const rich = Array.isArray(r?.layout) && r.layout.length > 0
          ? layoutFromServer(r.layout)
          : layoutFromLegacy(r.widgets);
        setSavedLayout(rich);
        setPendingLayout(rich);
      })
      .catch((e) => setConfigErr(e?.message || "Failed to load dashboard config"));
    // Wave 4B-3 P1: removed duplicate dashboardApi.myPermissions() —
    // `allowedPerms` is now sourced from AuthContext (single load).
  }, []);

  const activeLayout = editMode ? pendingLayout : savedLayout;
  const activeKeys = useMemo(
    () => activeLayout.map((it) => it.widget_key),
    [activeLayout]
  );

  useEffect(() => {
    if (activeKeys.length === 0) return;
    setLoadingSummary(true);
    dashboardApi.widgetSummary(activeKeys)
      .then((r) => {
        setPayloads(r?.widgets || {});
        setAsOf(r?.as_of || null);
      })
      .catch(() => {})
      .finally(() => setLoadingSummary(false));
  }, [activeKeys.join(",")]); // eslint-disable-line react-hooks/exhaustive-deps

  // ---- Edit Mode actions ----
  const enterEdit = useCallback(() => {
    setPreEditLayout(savedLayout);
    setPendingLayout(savedLayout);
    setEditMode(true);
  }, [savedLayout]);

  const cancelEdit = useCallback(() => {
    setPendingLayout(preEditLayout || savedLayout);
    setEditMode(false);
    setPreEditLayout(null);
  }, [preEditLayout, savedLayout]);

  const saveEdit = useCallback(async () => {
    setSaving(true);
    try {
      const r = await dashboardApi.saveLayout(pendingLayout);
      // Server echoes the persisted layout — normalize through the
      // same server-shape helper so sizes AND heights survive the
      // round-trip on this render (avoids a flash-of-default before
      // the next mount).
      const returned = Array.isArray(r?.layout) && r.layout.length > 0
        ? layoutFromServer(r.layout)
        : pendingLayout;
      setSavedLayout(returned);
      setPendingLayout(returned);
      setEditMode(false);
      setPreEditLayout(null);
      if (r?.persisted === false) {
        toast(
          r?.message ||
            "Layout saved for this session only (persistence disabled).",
          { duration: 4000 }
        );
      } else {
        toast.success("Layout saved.");
      }
    } catch (e) {
      toast.error("Failed to save layout.");
    } finally {
      setSaving(false);
    }
  }, [pendingLayout]);

  const restoreDefault = useCallback(async () => {
    try {
      const r = await dashboardApi.restoreRoleDefault();
      // Fleet Map UX Hardening — Restore Default is a COMMIT action.
      // The server has already cleared `dashboard_config`; leaving the
      // client in Edit Mode with a populated `pendingLayout` would let
      // a subsequent Save re-override with the same content and set
      // `is_user_override=true` again. Mirror savedLayout + exit Edit
      // Mode so the visible state matches server truth.
      const restored = Array.isArray(r?.layout) && r.layout.length > 0
        ? layoutFromServer(r.layout)
        : [];
      setSavedLayout(restored);
      setPendingLayout(restored);
      setEditMode(false);
      setPreEditLayout(null);
      toast(
        r?.message || "Restored role default.",
        { duration: 4000 }
      );
    } catch {
      // Fall back to the client-side role default from /dashboard/me
      // (in-session only; server may still hold an override).
      setPendingLayout(layoutFromLegacy(config?.role_default_widgets || []));
      toast("Restored role default (client fallback).", { duration: 4000 });
    }
  }, [config]);

  const handleReorder    = (next) => setPendingLayout(next);
  const handleResize     = (k, s) => setPendingLayout((p) => setSizeInLayout(p, k, s));
  const handleHeight     = (k, h) => setPendingLayout((p) => setHeightInLayout(p, k, h));
  const handleRemove     = (k)    => setPendingLayout((p) => removeWidget(p, k));
  const handleAdd        = (k)    => setPendingLayout((p) => addWidget(p, k));

  const perspective = config?.perspective_label;

  if (config?.is_future_shop_role) {
    return <FutureShopShell perspectiveLabel={perspective} />;
  }

  const dirty = editMode && !layoutsEqual(pendingLayout, preEditLayout || savedLayout);

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 lg:py-8" data-testid="page-dashboard">
      <div className="flex items-start justify-between gap-4 mb-2">
        <div className="flex-1 min-w-0">
          <DashboardGreeting user={user} perspectiveLabel={perspective} />
        </div>
        <div className="pt-1 shrink-0 flex items-center gap-2">
          <LiveTimestamp asOf={asOf} />
          {!editMode ? (
            <button
              type="button"
              data-testid="dashboard-edit-toggle"
              onClick={enterEdit}
              className="inline-flex items-center gap-1 text-[11.5px] font-medium border border-coach-silver hover:border-coach-blue text-carbon dark:text-road-white bg-white dark:bg-graphite px-2 py-1 rounded-sm"
            >
              <Pencil size={12} /> Edit Dashboard
            </button>
          ) : (
            <div
              data-testid="dashboard-edit-toolbar"
              className="flex items-center gap-1.5 flex-wrap justify-end"
            >
              <button
                type="button"
                data-testid="dashboard-edit-add"
                onClick={() => setShowPicker(true)}
                className="inline-flex items-center gap-1 text-[11.5px] border border-coach-silver hover:border-coach-blue px-2 py-1 rounded-sm"
              >
                <Plus size={12} /> Add Widget
              </button>
              <button
                type="button"
                data-testid="dashboard-edit-restore"
                onClick={restoreDefault}
                className="inline-flex items-center gap-1 text-[11.5px] border border-coach-silver hover:border-amber-hold px-2 py-1 rounded-sm"
              >
                <RotateCcw size={12} /> Restore Default
              </button>
              <button
                type="button"
                data-testid="dashboard-edit-cancel"
                onClick={cancelEdit}
                className="inline-flex items-center gap-1 text-[11.5px] border border-coach-silver hover:border-critical-red px-2 py-1 rounded-sm"
              >
                <X size={12} /> Cancel
              </button>
              <button
                type="button"
                data-testid="dashboard-edit-save"
                onClick={saveEdit}
                disabled={saving || !dirty}
                className="inline-flex items-center gap-1 text-[11.5px] font-medium bg-coach-blue hover:bg-deep-road-navy disabled:opacity-50 text-white px-2 py-1 rounded-sm"
              >
                <Save size={12} /> {saving ? "Saving…" : "Save Layout"}
              </button>
            </div>
          )}
        </div>
      </div>

      {editMode && (
        <div
          data-testid="dashboard-edit-banner"
          className="mt-2 mb-3 px-3 py-2 rounded-sm bg-amber-hold/10 border border-amber-hold/30 text-carbon dark:text-road-white text-[12px]"
        >
          <strong>Edit Mode.</strong> Drag cards to reorder, use the arrows for
          keyboard/mobile reordering, resize where supported. Persistence
          is currently in-session only.
        </div>
      )}

      {configErr && (
        <div
          data-testid="dashboard-config-error"
          className="bg-white dark:bg-graphite border border-critical-red/40 text-critical-red rounded-md p-4 mb-6 text-[13px]"
        >
          {configErr}
        </div>
      )}

      {!config && !configErr && (
        <div data-testid="dashboard-loading" className="mt-4 text-[12px] text-steel">
          Loading dashboard…
        </div>
      )}

      {config && activeLayout.length === 0 && !editMode && <EmptyDashboard />}

      <DashboardGrid
        layout={activeLayout}
        payloads={payloads}
        editMode={editMode}
        onReorder={handleReorder}
        onResize={handleResize}
        onHeight={handleHeight}
        onRemove={handleRemove}
      />

      {loadingSummary && activeKeys.length > 0 && Object.keys(payloads).length === 0 && (
        <div data-testid="dashboard-loading" className="mt-4 text-[12px] text-steel">
          Loading widgets…
        </div>
      )}

      <AddWidgetPicker
        open={showPicker}
        onClose={() => setShowPicker(false)}
        onPick={handleAdd}
        currentKeys={activeKeys}
        allowedPermissions={allowedPerms}
      />
    </div>
  );
}

const EmptyDashboard = () => (
  <div
    data-testid="dashboard-empty"
    className="bg-white dark:bg-graphite border border-coach-silver dark:border-graphite rounded-md p-8 text-center"
  >
    <LayoutDashboard size={22} className="text-steel mx-auto mb-3" />
    <div className="font-display text-lg text-carbon dark:text-road-white">No widgets configured</div>
    <div className="text-[13px] text-steel mt-2 max-w-md mx-auto">
      Enter Edit Mode and use "Add Widget" to build your dashboard, or
      "Restore Default" to load your role's shipped layout.
    </div>
  </div>
);

// Expose legacy conversion for admin-side reuse in a future wave.
export { layoutToLegacy };
